/**
 * The character stat block the combat layer reads from, and the resistance
 * arithmetic that sits between raw damage and the damage a defender takes.
 *
 * Deliberately narrow: this file describes what a *combatant* is, not what an
 * *item* is. Equipment lives in the loot layer; it satisfies `CombatantStats`
 * by summing whatever it wants into these plain numbers. Nothing here imports
 * anything but types — the core stays pure.
 *
 * Every table is derived from `DAMAGE_TYPES` / `ATTRIBUTE_NAMES` so adding a
 * sixth damage type is a one-line change rather than a hunt through branches.
 */
/**
 * Every damage type in the game, in the canonical order rolls are drawn.
 *
 * This order is part of the replay contract: `resolveAttack` draws one damage
 * roll per type in exactly this sequence, so reordering the tuple reseats every
 * seed anyone has ever played.
 */
export const DAMAGE_TYPES = ['physical', 'fire', 'cold', 'lightning', 'poison'];
/** The four D2 primary attributes. */
export const ATTRIBUTE_NAMES = ['strength', 'dexterity', 'vitality', 'energy'];
/** Resistance at or above this point absorbs the type completely. Monsters are the ones that reach it. */
export const IMMUNITY_THRESHOLD = 100;
/** D2's player resistance cap. Monsters carry a higher cap of their own, which is why immunities exist at all. */
export const DEFAULT_RESISTANCE_CAP = 75;
/** Resistance floor: past this point extra negative resistance stops amplifying. */
export const RESISTANCE_FLOOR = -100;
/** Attack rating gained per point of dexterity. */
export const ATTACK_RATING_PER_DEXTERITY = 5;
/** Dexterity points needed for one point of defense. */
export const DEXTERITY_PER_DEFENSE = 4;
/**
 * Attack animation length a neutral profile assumes, in frames.
 *
 * A placeholder: the real per-class, per-weapon animation lengths come from
 * D2's animation tables and are not decided yet (TODO #22).
 */
export const DEFAULT_ATTACK_ANIMATION_FRAMES = 16;
/**
 * The frame of a neutral attack animation the blow actually lands on — D2's
 * **ActionFlag**.
 *
 * A placeholder in exactly the sense `DEFAULT_ATTACK_ANIMATION_FRAMES` is one,
 * and for the same reason: D2 stores an ActionFlag per animation, in
 * `AnimData.D2`, and the Amazon Basin's own write-up says it "is set to quite
 * different moments during the animation" from one class and weapon to the next
 * — so there is no single D2 number to copy, only a per-animation table this
 * project does not have yet (`TODO.md` #22, #30).
 *
 * Half-way is the neutral choice, and it is the only one that gives a swing as
 * much anticipation as follow-through. It is deliberately **not zero**: a blow
 * on frame 0 is a swing with no anticipation at all, which is what this game had
 * before the hit frame existed and what `TODO.md` #483 exists to end.
 *
 * Source: Amazon Basin, "Frames and Animations"
 * (https://www.mannm.org/d2library/faqtoids/frames_eng.html); the per-class
 * ActionFlag tables it points at are in the stat tables
 * (https://www.mannm.org/d2library/faqtoids/stattab_eng.html).
 */
export const DEFAULT_ATTACK_HIT_FRAME = 8;
const clamp = (value, low, high) => Math.min(Math.max(value, low), high);
const buildRecord = (keys, pick) => Object.fromEntries(keys.map((key) => [key, pick(key)]));
/** Builds a complete per-damage-type record. The one place damage tables are constructed. */
export const mapDamageTypes = (pick) => buildRecord(DAMAGE_TYPES, pick);
/** Completes a partial damage table; unmentioned types are zero. */
export const createDamage = (partial = {}) => mapDamageTypes((type) => partial[type] ?? 0);
/** Completes a partial resistance table; unmentioned types are zero. */
export const createResistances = (partial = {}) => mapDamageTypes((type) => partial[type] ?? 0);
/** Completes a partial weapon damage table; unmentioned types deal nothing. */
export const createDamageRanges = (partial = {}) => mapDamageTypes((type) => partial[type] ?? { min: 0, max: 0 });
/** Completes a partial attribute set; unmentioned attributes are zero. */
export const createAttributes = (partial = {}) => buildRecord(ATTRIBUTE_NAMES, (name) => partial[name] ?? 0);
/** Completes a partial attack profile against a neutral one. */
export const createAttackProfile = (input = {}) => ({
    damage: createDamageRanges(input.damage),
    weaponSpeedModifier: input.weaponSpeedModifier ?? 0,
    increasedAttackSpeed: input.increasedAttackSpeed ?? 0,
    skillAttackSpeed: input.skillAttackSpeed ?? 0,
    animationFrames: input.animationFrames ?? DEFAULT_ATTACK_ANIMATION_FRAMES,
    hitFrame: input.hitFrame ?? DEFAULT_ATTACK_HIT_FRAME,
    physicalDamagePerAttribute: createAttributes(input.physicalDamagePerAttribute),
    criticalChance: input.criticalChance ?? 0,
});
/** Completes a partial stat block against a neutral combatant that skews no roll. */
export const createCombatantStats = (input = {}) => ({
    level: input.level ?? 1,
    attributes: createAttributes(input.attributes),
    attackRating: input.attackRating ?? 0,
    defense: input.defense ?? 0,
    resistances: createResistances(input.resistances),
    resistanceCap: input.resistanceCap ?? DEFAULT_RESISTANCE_CAP,
    blockRating: input.blockRating ?? 0,
    attack: createAttackProfile(input.attack),
});
/**
 * Stacks resistance sources. Every source counts at full value — capping
 * happens once, on the total, in `resolveResistance`.
 */
export const stackResistance = (sources) => sources.reduce((total, source) => total + source, 0);
/**
 * Resolves a stacked resistance total, in this order and no other:
 *
 *   1. immunity is tested against the *uncapped* total (`>= 100`), so a monster
 *      stays immune however low the defender's cap is, and so a curse that
 *      shaves the raw total is what breaks the immunity;
 *   2. the total is clamped to `cap` — over-stacked resistance is not wasted, it
 *      absorbs penalties before the cap ever sees them;
 *   3. the total is floored at `RESISTANCE_FLOOR` so amplification cannot run away.
 *
 * An immune result reports an effective 100, which makes mitigation a single
 * formula with no immunity branch in it.
 */
export const resolveResistance = (total, cap = DEFAULT_RESISTANCE_CAP) => {
    const immune = total >= IMMUNITY_THRESHOLD;
    return {
        total,
        immune,
        effective: immune ? IMMUNITY_THRESHOLD : clamp(total, RESISTANCE_FLOOR, cap),
    };
};
/**
 * The one damage-mitigation formula: `taken = dealt * (100 - resistance) / 100`,
 * rounded down to whole points. Immunity falls out of it (100 resistance leaves
 * zero) and so does amplification (−50 resistance leaves 150%).
 */
export const mitigate = (amount, resistance) => Math.floor((amount * (IMMUNITY_THRESHOLD - resistance.effective)) / IMMUNITY_THRESHOLD);
/**
 * Applies a defender's resistances to a full damage table, type by type.
 *
 * Reports the immunities alongside the numbers because the caller would
 * otherwise have to resolve every resistance a second time to find out which
 * zero meant "immune" and which merely meant "no damage of that type".
 */
export const applyResistances = (damage, resistances, cap = DEFAULT_RESISTANCE_CAP) => {
    const resolved = mapDamageTypes((type) => resolveResistance(resistances[type], cap));
    return {
        damage: mapDamageTypes((type) => mitigate(damage[type], resolved[type])),
        immune: DAMAGE_TYPES.filter((type) => damage[type] > 0 && resolved[type].immune),
    };
};
/**
 * Attack rating from dexterity, on top of a class base.
 *
 * D2 gives 5 attack rating per point of dexterity; the character sheet's
 * familiar `dex * 5 - 35` is this with a base of −35. The base is data because
 * it is class-dependent and class tables are not decided yet (TODO #22).
 */
export const deriveAttackRating = (attributes, base) => base + ATTACK_RATING_PER_DEXTERITY * attributes.dexterity;
/** Defense from dexterity, on top of worn armour: D2 grants 1 defense per 4 dexterity. */
export const deriveDefense = (attributes, armorDefense) => armorDefense + Math.floor(attributes.dexterity / DEXTERITY_PER_DEFENSE);
const deriveResource = (growth, attributePoints, level) => growth.base + growth.perAttributePoint * attributePoints + growth.perLevel * (level - 1);
/** Maximum life: vitality is the governing attribute. */
export const deriveLife = (attributes, growth, level) => deriveResource(growth, attributes.vitality, level);
/** Maximum mana: energy is the governing attribute. */
export const deriveMana = (attributes, growth, level) => deriveResource(growth, attributes.energy, level);
//# sourceMappingURL=stats.js.map